import React, { useEffect, useState } from "react";
import { Lock, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { confirmPasswordReset } from "@/lib/api";

/**
 * Stand-alone "Set a new password" page rendered when the user lands on
 * `${PUBLIC_APP_URL}/reset?token=xyz` from a password-reset email.
 *
 * Self-contained: doesn't pull from AuthContext (the user is unauthenticated
 * by design here). On success we route back to `/` so they can sign in with
 * the new password.
 */
export default function PasswordResetView() {
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [doneEmail, setDoneEmail] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token") || "";
    setToken(t);
    if (!t) setErr("Missing reset token. Open the link from your email again.");
  }, []);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const res = await confirmPasswordReset(token, password);
      setDoneEmail(res?.email || "");
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't reset right now. Try again.");
    } finally {
      setBusy(false);
    }
  }

  function backToApp() {
    window.location.href = "/";
  }

  return (
    <div className="public-share-shell" data-testid="password-reset-view">
      <div className="reset-card">
        <div className="reset-card-brand">
          <div className="brand-mark">VA</div>
          <div className="reset-card-brand-text">
            <div className="reset-card-eyebrow">CLAIMOPS VA</div>
            <h1 className="reset-card-title">{done ? "Password updated" : "Set a new password"}</h1>
          </div>
        </div>

        {done ? (
          <div className="reset-card-success" data-testid="reset-success">
            <CheckCircle size={48} weight="duotone" />
            <p>
              You're all set{doneEmail ? <> for <strong>{doneEmail}</strong></> : ""}. Sign in with your new
              password to pick up where you left off.
            </p>
            <button
              type="button"
              className="btn btn--accent"
              onClick={backToApp}
              data-testid="reset-back-to-app"
            >
              Sign in <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="reset-card-form">
            <p className="reset-card-desc">
              Pick a strong password — at least 8 characters. You'll be signed out of any
              existing sessions on other devices.
            </p>
            <label className="field">
              <span className="field-label"><Lock size={12} weight="bold" /> NEW PASSWORD</span>
              <input
                type="password"
                className="field-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                autoFocus
                disabled={!token}
                data-testid="reset-password"
              />
            </label>
            <label className="field">
              <span className="field-label"><Lock size={12} weight="bold" /> CONFIRM</span>
              <input
                type="password"
                className="field-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Retype it"
                required
                disabled={!token}
                data-testid="reset-confirm"
              />
            </label>
            {err && <div className="auth-prompt-error" data-testid="reset-error">{err}</div>}
            <button
              type="submit"
              className="btn btn--accent auth-prompt-submit"
              disabled={busy || !token}
              data-testid="reset-submit"
            >
              {busy ? "Saving..." : "Update password"}
            </button>
            <button
              type="button"
              className="link-btn auth-prompt-link"
              onClick={backToApp}
              data-testid="reset-cancel"
            >
              Back to app
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
